import React, { useCallback, useRef, useState, useEffect } from 'react';
import { SidebarMenu } from './sidebar-menu';
import { getMenuData } from '../../utils/menuHelper';
import { getActiveRuleSetFromUrl } from '../../utils/UrlUtil';
import { noop, noopHook } from '../../utils/noop';
import { Dict } from '@chakra-ui/utils';
import { useTheme } from '@everfi/design-system';

interface User {
  rule_sets: Array<any>;
}

interface Environment {
  MENU_DOMAIN_FOR_FRONT_END: string;
}

interface SidebarProps {
  currentUrl?: string;
  organization?: object;
  user?: User;
  environment: Environment;
  slug?: string;
  useNavigate?: any;
  useSearchParams?: () => [URLSearchParams | null];
  options?: string[];
  theme?: Dict;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentUrl,
  organization,
  user,
  environment,
  slug,
  useNavigate = noop,
  useSearchParams = noopHook,
  options,
  theme,
}) => {
  const currentRuleSetName = useRef<string | null>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  if (searchParams) {
    currentRuleSetName.current = searchParams.get('current_rule_set_name');
  }

  const buildMenuData = useCallback(() => {
    if (!user) return null;
    const isAdmin = !organization;
    if (!currentRuleSetName.current && !isAdmin) {
      currentRuleSetName.current = getActiveRuleSetFromUrl(user.rule_sets, currentUrl).name;
    }
    return getMenuData({
      slug,
      environment,
      user,
      organization,
      currentRuleSetName: currentRuleSetName.current,
      isAdmin,
      currentUrl,
    });
  }, [currentUrl, environment, organization, slug, user]);

  const [menuData, setMenuData] = useState(buildMenuData());

  useEffect(() => {
    if (user && slug) {
      setMenuData(buildMenuData());
    }
  }, [setMenuData, slug, user, buildMenuData]);

  const currentTheme = theme || useTheme('chakraUiTheme').activeThemeDict;

  if (menuData) {
    return (
      <SidebarMenu
        currentRuleSets={user ? user.rule_sets : []}
        currentUrl={currentUrl || window.location.href}
        frontEndDomain={environment.MENU_DOMAIN_FOR_FRONT_END}
        menuData={menuData}
        navigate={navigate}
        options={options}
        theme={currentTheme}
      />
    );
  } else {
    return <nav id="sidebar" data-component-type="sidebar" className="nav sidebar" />;
  }
};

Sidebar.displayName = 'Sidebar';
